import { Alert, Option, Select, useToastProvider } from "@deere/fuel-react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import {
  ChangeEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { SavedAddress } from "../../../../api/orders/orderInterfaces";
import {
  Validation,
  ValidationReport,
} from "../../../../api/validation/validationInterfaces";
import useXtremeBffApi from "../../../../api/xtremeBff/useXtremeBff";
import xtremeBffApi from "../../../../api/xtremeBff/xtremeBffApi";
import { UpdateShipToAddressResponse } from "../../../../api/xtremeBff/xtremeBffInterfaces";
import LoadingIndicator from "../../../../components/LoadingIndicator/LoadingIndicator";
import { STATE_IGNORED_REGIONS } from "../../../../constant";
import { ConfigContext } from "../../../../context/ConfigContext";
import { OrderContext } from "../../../../context/OrderContext/OrderContext";
import { Address } from "../../types";

export interface EditAddressModalType {
  open: boolean;
  address: Address;
  closeModal: () => void;
  orderNumber: string;
  accountId: string;
  updateAddress: (res: UpdateShipToAddressResponse) => void;
  isCleanserAddressValidationEnabled?: boolean;
  isV2AddressValidationEnabled?: boolean;
  setWarningEditModel?: (value: boolean) => void;
}

type Country = {
  code: string;
  name: string;
  rules?: string;
  regions?: Region[];
};

type Region = {
  code: string;
  name: string;
};

const EditAddressModal = ({
  open,
  closeModal,
  address,
  orderNumber,
  accountId,
  updateAddress,
  isCleanserAddressValidationEnabled,
  isV2AddressValidationEnabled,
  setWarningEditModel,
}: EditAddressModalType) => {
  const theme = useTheme();
  const { oktaAuth } = useOktaAuth();
  const {
    XTREME_BFF_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE,
    ENABLE_ADDRESS_CONFIRMATION_CHECKBOX,
    ENABLE_ZIP_CODE_SPACES,
  } = useContext(ConfigContext);
  const {
    shipToAddressValidationReport,
    v2AddressValidationReport,
    setShipToAddressValidationReport,
    setV2AddressValidationReport,
  } = useContext(OrderContext);
  const { t, i18n } = useTranslation(["orders", "common"]);
  const { enqueueToast } = useToastProvider();
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>(
    address.country?.toUpperCase() ?? ""
  );
  const [selectedRegion, setSelectedRegion] = useState<string>(
    address.state ?? ""
  );
  const COUNTRY_WITH_NO_DEFAULT_SELECTION = ["AR"];

  const [name, setName] = useState<string>(address.name ?? "");
  const [addressLineOne, setAddressLineOne] = useState<string>(
    address.addressLine1 ?? ""
  );
  const [addressLineTwo, setAddressLineTwo] = useState<string>(
    address.addressLine2 ?? ""
  );
  const [cityName, setCityName] = useState<string>(address.city ?? "");
  const [zipCode, setZipCode] = useState<string>(address.postalCode ?? "");
  const [phoneNumber, setPhoneNumber] = useState<string>(
    address.phoneNumber ?? ""
  );

  const [isCountryInvalid, setCountryInvalid] = useState<boolean>(false);
  const [isStateInvalid, setStateInvalid] = useState<boolean>(false);
  const [isCityInvalid, setCityInvalid] = useState<boolean>(!cityName);
  const [isZipCodeInvalid, setZipCodeInvalid] = useState<boolean>(false);
  const [zipCodeValidationDescription, setZipCodeValidationDescription] =
    useState<string>("");
  const [isJurisdictionInvalid, setJurisdictionInvalid] =
    useState<boolean>(false);
  const [addressLineOneValidation, setAddressLineOneValidation] =
    useState<string>("");
  const [confirmAddress, setConfirmAddress] = useState<boolean>(false);
  const xtremeBffApiHook = useXtremeBffApi();
  const style = {
    [`.Mui-error`]: {
      borderColor: theme.tokens.FuelColorAlertErrorIcon,
      outline: "none !important",
    },
    ".Mui-error+select+button": {
      boxShadow: `inset 0 0 0 1px ${theme.tokens.FuelColorAlertErrorIcon}`,
    },
  };

  const isAddressLineOneInvalid = addressLineOneValidation !== "";

  const getZipCodeExample = (description?: string) => {
    if (!description) {
      return "";
    }

    const exampleIndex = description.toLowerCase().indexOf("example");
    if (exampleIndex === -1) {
      return "";
    }

    const separatorIndex = description.indexOf(":", exampleIndex);
    if (separatorIndex === -1) {
      return "";
    }

    let example = description.slice(separatorIndex + 1).trim();
    while (example.endsWith(".")) {
      example = example.slice(0, -1);
    }

    return example ? `${example}.` : "";
  };

  const getZipCodeValidationMessage = () => {
    const defaultMessage = t("orders:editAddressModal.zipCode.invalid.message");
    if (!zipCodeValidationDescription) {
      return defaultMessage;
    }

    return t("orders:editAddressModal.zipCode.invalid.message.withExample", {
      example: zipCodeValidationDescription,
    });
  };

  const addressLineOneHelperTextMap = new Map([
    [
      "STREET_NAME_VALIDATION",
      "orders:editAddressModal.addressLineOne.streetName.invalid.message",
    ],
    [
      "STREET_COMPONENT_VALIDATION",
      "orders:editAddressModal.addressLineOne.streetComponent.invalid.message",
    ],
    [
      "HOUSE_NUMBER_VALIDATION",
      "orders:editAddressModal.addressLineOne.houseNumber.invalid.message",
    ],
    [
      "INCOMPLETE_ADDRESS_VALIDATION",
      "orders:editAddressModal.addressLineOne.incompleteAddress.invalid.message",
    ],
    [
      "ADDRESS_LINE_ONE_VALIDATION",
      "orders:editAddressModal.addressLineOne.invalid.message",
    ],
  ]);
  const [addressError, setAddressError] = useState<string>("");
  const [suggestedList, setSuggestedList] = useState<
    Array<{
      id: string;
      name: string;
      addressLineOne: string;
      addressLineTwo: string;
      cityName: string;
      stateOrProvinceName: string;
      zipCode: string;
      countryCode: string;
      phoneNumber: string;
    }>
  >([]);
  const [selected, setSelected] = useState<string>("");
  const firstSuggestedRef = useRef<HTMLInputElement>(null);
  const focusAttemptCount = useRef(0);
  const [isFormModified, setIsFormModified] = useState<boolean>(false);
  const [hasProcessedInitialValidation, setHasProcessedInitialValidation] =
    useState<boolean>(false);
  const [isSelectedFromSuggestion, setIsSelectedFromSuggestion] =
    useState<boolean>(false);

  useEffect(() => {
    if (addressError) {
      setConfirmAddress(false);
    }
  }, [addressError]);

  useEffect(() => {
    if (
      shipToAddressValidationReport &&
      isFormModified &&
      !hasProcessedInitialValidation
    ) {
      handleValidationError(shipToAddressValidationReport, false);

      if (isV2AddressValidationEnabled) {
        const hasSuggestions =
          (v2AddressValidationReport?.suggestedValidAddresses?.length ?? 0) > 0;
        if (
          shipToAddressValidationReport.status === "FAILURE" &&
          !hasSuggestions
        ) {
          setAddressError("No valid address found.");
        } else if (hasSuggestions) {
          setAddressError("");
        }
      }
    }
  }, [
    shipToAddressValidationReport,
    v2AddressValidationReport,
    isFormModified,
    hasProcessedInitialValidation,
    isV2AddressValidationEnabled,
  ]);

  useEffect(() => {
    if (
      v2AddressValidationReport &&
      isFormModified &&
      !hasProcessedInitialValidation
    ) {
      handleAllAddressValidationFailures(v2AddressValidationReport);
    }
  }, [
    v2AddressValidationReport,
    isFormModified,
    hasProcessedInitialValidation,
  ]);

  useEffect(() => {
    if (open) {
      const hasShipToValidationErrors =
        shipToAddressValidationReport?.status === "FAILURE" &&
        shipToAddressValidationReport?.validations?.length > 0;

      const hasV2ValidationErrors =
        v2AddressValidationReport?.status === "FAILURE";

      if (orderNumber && (hasShipToValidationErrors || hasV2ValidationErrors)) {
        if (hasShipToValidationErrors) {
          handleValidationError(shipToAddressValidationReport, true);

          if (isV2AddressValidationEnabled) {
            const hasSuggestions =
              (v2AddressValidationReport?.suggestedValidAddresses?.length ??
                0) > 0;
            if (!hasSuggestions) {
              setAddressError("No valid address found.");
            }
          }
        }

        if (hasV2ValidationErrors) {
          handleAllAddressValidationFailures(v2AddressValidationReport);
        }

        setHasProcessedInitialValidation(true);
      } else {
        setShipToAddressValidationReport({
          status: "SUCCESS",
          validations: [],
        });
        setV2AddressValidationReport({
          status: "SUCCESS",
          suggestedValidAddresses: [],
        });

        setAddressError("");
        setSuggestedList([]);
        setSelected("");
        setCountryInvalid(false);
        setStateInvalid(false);
        setCityInvalid(false);
        setZipCodeInvalid(false);
        setZipCodeValidationDescription("");
        setJurisdictionInvalid(false);
        setAddressLineOneValidation("");
        setConfirmAddress(false);
        setHasProcessedInitialValidation(false);
      }

      setIsFormModified(false);
      loadCountries();
    }
  }, [
    open,
    orderNumber,
    setShipToAddressValidationReport,
    setV2AddressValidationReport,
  ]);

  useLayoutEffect(() => {
    if (open && suggestedList.length > 0) {
      focusAttemptCount.current = 0;
      const attemptFocus = () => {
        if (firstSuggestedRef.current) {
          firstSuggestedRef.current.focus();
          if (
            document.activeElement !== firstSuggestedRef.current &&
            focusAttemptCount.current < 3
          ) {
            focusAttemptCount.current += 1;
            requestAnimationFrame(attemptFocus);
          }
        }
      };
      requestAnimationFrame(attemptFocus);
    }
  }, [open, suggestedList.length]);

  const isReadOnly = suggestedList.length > 0;
  const getAccessToken = (): string => {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) return "";
    return authToken;
  };
  const navigate = useNavigate();

  const loadCountries = async () => {
    setLoading(true);
    try {
      const res = await xtremeBffApi.getCountriesAndRegions(
        XTREME_BFF_SERVICE_URL,
        getAccessToken()
      );
      setCountries(res.countries);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };
  const getCountryOptions = () =>
    countries.map((country) => (
      <Option key={country.code} value={country.code}>
        {country.name}
      </Option>
    ));

  const getRegionOptions = () =>
    getRegions().map((region) => (
      <Option key={region.code} value={region.code}>
        {region.name}
      </Option>
    ));
  const getRegions = () => {
    const country = countries.find((item) => item.code === selectedCountry);
    if (!country || !country.regions) return [];
    return COUNTRY_WITH_NO_DEFAULT_SELECTION.includes(country.code)
      ? [...country.regions]
      : [
          { code: "", name: t("orders:editAddressModal.state.notSelected") },
          ...country.regions,
        ];
  };

  const getValue = (value: string | undefined) => value ?? "";

  const isSame = (
    firstValue: string | undefined,
    secondValue: string | undefined
  ) => firstValue?.toUpperCase().trim() === secondValue?.toUpperCase().trim();

  const handleIfFieldIsSame = (
    originalValue: string | undefined,
    currentValue: string | undefined
  ): boolean => {
    if (
      isCleanserAddressValidationEnabled &&
      isSame(originalValue, getValue(currentValue))
    ) {
      handleValidationError(shipToAddressValidationReport);
      return true;
    }

    return false;
  };

  const onCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const countryCode = event.target.value as string;
    setSelectedCountry(countryCode);
    setCountryInvalid(false);
    setSelectedRegion("");
    setJurisdictionInvalid(false);
    setIsFormModified(true);
    setHasProcessedInitialValidation(false);
    setIsSelectedFromSuggestion(false);
    setConfirmAddress(false);
  };

  const onRegionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const region = event.target.value as string;
    setSelectedRegion(region);

    if (handleIfFieldIsSame(region, address.state)) return;

    setStateInvalid(false);
    setJurisdictionInvalid(false);
    setIsFormModified(true);
    setHasProcessedInitialValidation(false);
    setIsSelectedFromSuggestion(false);
    setConfirmAddress(false);
  };

  const onAddressLineOneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const enteredAddressLineOne = event.target.value as string;

    setAddressLineOne(enteredAddressLineOne);

    if (handleIfFieldIsSame(enteredAddressLineOne, address.addressLine1))
      return;

    setAddressLineOneValidation("");
    setIsFormModified(true);
    setHasProcessedInitialValidation(false);
    setIsSelectedFromSuggestion(false);
    setConfirmAddress(false);
  };

  const onCityNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const city = event.target.value as string;

    setCityName(city);

    if (city !== "") {
      if (handleIfFieldIsSame(city, address.city)) return;

      setCityInvalid(false);
      setJurisdictionInvalid(false);
    }
    setIsFormModified(true);
    setHasProcessedInitialValidation(false);
    setIsSelectedFromSuggestion(false);
    setConfirmAddress(false);
  };

  const onZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const parsedZipCode = sanitizeZipCode(event.target.value);

    setZipCode(parsedZipCode);

    if (handleIfFieldIsSame(parsedZipCode, address.postalCode)) return;

    setZipCodeInvalid(false);
    setJurisdictionInvalid(false);
    setIsFormModified(true);
    setHasProcessedInitialValidation(false);
    setIsSelectedFromSuggestion(false);
    setConfirmAddress(false);
  };

  const sanitizeZipCode = (value: string, trimTrailingSpace = false) => {
    const sanitizedZipCode = value.replace(/[^\w- ]/gi, "").toUpperCase();

    if (!ENABLE_ZIP_CODE_SPACES) {
      return sanitizedZipCode.replace(/ /g, "");
    }

    const normalizedZipCode = sanitizedZipCode
      .replace(/^ +/, "")
      .replace(/ {2,}/g, " ");

    return trimTrailingSpace ? normalizedZipCode.trimEnd() : normalizedZipCode;
  };

  const handleAllAddressValidationFailures = (
    validationReportData: typeof v2AddressValidationReport
  ) => {
    const suggestions = validationReportData?.suggestedValidAddresses ?? [];

    if (suggestions.length > 0) {
      setSuggestedList(
        suggestions.map((addr: any, idx: number) => ({
          id: String(idx),
          name: addr.name ?? "",
          addressLineOne: addr.addressLineOne ?? "",
          addressLineTwo: addr.addressLineTwo ?? "",
          cityName: addr.cityName ?? "",
          stateOrProvinceName: addr.regionCode ?? "",
          zipCode: addr.postalCode ?? "",
          countryCode: addr.countryCode ?? "",
          phoneNumber: addr.phoneNumber ?? "",
        }))
      );
    }

    if (
      validationReportData?.status === "FAILURE" &&
      suggestions.length === 0
    ) {
      setAddressError("No valid address found.");
    } else if (suggestions.length > 0) {
      setAddressError("");
    }
  };

  const handleValidationError = (
    validationReport: ValidationReport,
    isInitialLoad: boolean = false
  ) => {
    if (!isFormModified && !isInitialLoad) {
      return;
    }

    type ValidationCategory =
      | "COUNTRY_VALIDATION"
      | "REGION_VALIDATION"
      | "CITY_VALIDATION"
      | "POSTAL_CODE_VALIDATION"
      | "STREET_NAME_VALIDATION"
      | "STREET_COMPONENT_VALIDATION"
      | "HOUSE_NUMBER_VALIDATION"
      | "INCOMPLETE_ADDRESS_VALIDATION"
      | "JURISDICTION_VALIDATION"
      | "ADDRESS_LINE_ONE_VALIDATION";

    const setValidationError = (validation: ValidationCategory) => {
      const validationMap = new Map([
        ["COUNTRY_VALIDATION", setCountryInvalid],
        ["REGION_VALIDATION", setStateInvalid],
        ["CITY_VALIDATION", setCityInvalid],
        ["POSTAL_CODE_VALIDATION", setZipCodeInvalid],
        ["JURISDICTION_VALIDATION", setJurisdictionInvalid],
      ]);

      if (validationMap.has(validation)) {
        validationMap.get(validation)!(true);
        return;
      }

      setAddressLineOneValidation(validation);
    };

    validationReport?.validations?.forEach((validation: Validation) => {
      if (validation.category === "POSTAL_CODE_VALIDATION") {
        setZipCodeValidationDescription(
          validation.status === "FAILURE"
            ? getZipCodeExample(validation.description)
            : ""
        );
      }

      if (validation.status === "FAILURE")
        setValidationError(validation.category as ValidationCategory);
    });
  };

  const skipStateForZipCode = () =>
    STATE_IGNORED_REGIONS.some((region) => zipCode.match(`^${region}.*`));
  const isStateFieldEnabled = () =>
    getRegions().length > 0 && !skipStateForZipCode();

  const setUpdatedAddress = (shipToAddress: SavedAddress) => {
    setAddressLineOne(shipToAddress.addressLineOne!);
    setAddressLineTwo(shipToAddress.addressLineTwo!);
    setCityName(shipToAddress.cityName!);
    setSelectedRegion(shipToAddress.stateOrProvinceName!);
    setZipCode(shipToAddress.zipCode!);
    setPhoneNumber(shipToAddress.phoneNumber!);
  };

  const resetValidationErrors = () => {
    setAddressLineOneValidation("");
    setStateInvalid(false);
    setCityInvalid(false);
    setZipCodeInvalid(false);
    setZipCodeValidationDescription("");
    setJurisdictionInvalid(false);
  };

  const showSuccessToast = () => {
    enqueueToast({
      variant: "success",
      message: t("orders:editAddressModal.update.success.toast"),
      isCloseable: true,
    });
  };

  const isOrderServiceApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE;

  const handleSubmitWithCleanserValidation = () => {
    const updatedAddress: SavedAddress = {
      name,
      addressLineOne,
      addressLineTwo,
      cityName,
      zipCode,
      phoneNumber,
      stateOrProvinceName: isStateFieldEnabled() ? selectedRegion : "",
      countryCode: selectedCountry,
    };
    setLoading(true);
    (isOrderServiceApiAuthEnable
      ? xtremeBffApiHook.updateShipToAddressWithCleanser(
          XTREME_BFF_SERVICE_URL,
          getAccessToken(),
          oktaAuth.getIdToken() ?? "",
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language
        )
      : xtremeBffApi.updateShipToAddressWithCleanser(
          XTREME_BFF_SERVICE_URL,
          getAccessToken(),
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language
        )
    )
      .then((response) => {
        response.isDealerAddressChanged = true;
        const { shipToAddress } = response;
        updateAddress(response);
        setUpdatedAddress(shipToAddress);
        resetValidationErrors();
        showSuccessToast();

        setLoading(false);
        closeModal();
      })
      .catch((error) => {
        setLoading(false);
        if (setWarningEditModel && error.response.status === 409) {
          closeModal();
          setWarningEditModel(true);
          navigate(`/home`);
        } else if (error.response.status === 422) {
          handleValidationError(error.response.data);
        } else {
          enqueueToast({
            variant: "error",
            message: t("orders:editAddressModal.update.failure.toast"),
            isCloseable: true,
          });
          closeModal();
        }
      });
  };

  const handleSubmitV2AddressValidation = () => {
    const updatedAddress: SavedAddress = {
      name,
      addressLineOne,
      addressLineTwo,
      cityName,
      zipCode,
      phoneNumber,
      stateOrProvinceName: isStateFieldEnabled() ? selectedRegion : "",
      countryCode: selectedCountry,
    };
    setLoading(true);
    const selectedFromSuggestion = isSelectedFromSuggestion && selected !== "";
    const userConfirmed = confirmAddress;
    (isOrderServiceApiAuthEnable
      ? xtremeBffApiHook.updateShipToAddressV2(
          XTREME_BFF_SERVICE_URL,
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language,
          selectedFromSuggestion,
          userConfirmed
        )
      : xtremeBffApi.updateShipToAddressV2(
          XTREME_BFF_SERVICE_URL,
          getAccessToken(),
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language,
          selectedFromSuggestion,
          userConfirmed
        )
    )
      .then((response) => {
        const suggestedValidAddresses = response.suggestedValidAddresses || [];
        setAddressError("");
        if (suggestedValidAddresses.length === 0) {
          response.isDealerAddressChanged = true;
          const { shipToAddress } = response;
          updateAddress(response);
          setUpdatedAddress(shipToAddress);
          resetValidationErrors();
          showSuccessToast();
          setLoading(false);
          closeModal();
          setSuggestedList([]);
        } else {
          setSuggestedList(
            suggestedValidAddresses.map((addr: SavedAddress, idx: number) => ({
              id: String(idx),
              name: addr.name ?? "",
              addressLineOne: addr.addressLineOne ?? "",
              addressLineTwo: addr.addressLineTwo ?? "",
              cityName: addr.cityName ?? "",
              stateOrProvinceName: addr.stateOrProvinceName ?? "",
              zipCode: addr.zipCode ?? "",
              countryCode: addr.countryCode ?? "",
              phoneNumber: addr.phoneNumber ?? "",
            }))
          );
          setSelected("");
          setHasProcessedInitialValidation(true);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setAddressError("No valid address found.");
        setSuggestedList([]);
        setHasProcessedInitialValidation(true);
        if (setWarningEditModel && error.response.status === 409) {
          closeModal();
          setWarningEditModel(true);
          navigate(`/home`);
        } else if (error.response.status === 422) {
          handleValidationError(error.response.data);
        }
      });
  };
  const handleSubmit = () => {
    const finalizedZipCode = sanitizeZipCode(zipCode, true);
    const updatedAddress: SavedAddress = {
      name,
      addressLineOne,
      addressLineTwo,
      cityName,
      zipCode: finalizedZipCode,
      phoneNumber,
      stateOrProvinceName: isStateFieldEnabled() ? selectedRegion : "",
      countryCode: selectedCountry,
    };
    setLoading(true);
    (isOrderServiceApiAuthEnable
      ? xtremeBffApiHook.updateShipToAddress(
          XTREME_BFF_SERVICE_URL,
          getAccessToken(),
          oktaAuth.getIdToken() ?? "",
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language
        )
      : xtremeBffApi.updateShipToAddress(
          XTREME_BFF_SERVICE_URL,
          getAccessToken(),
          updatedAddress,
          orderNumber,
          accountId,
          i18n.language
        )
    )
      .then((response) => {
        if (confirmAddress) {
          setAddressError("");
          setIsFormModified(false);
          setConfirmAddress(false);
        }
        response.isDealerAddressChanged = true;
        updateAddress(response);
        enqueueToast({
          variant: "success",
          message: t("orders:editAddressModal.update.success.toast"),
          isCloseable: true,
        });
        setLoading(false);
        closeModal();
      })
      .catch((error) => {
        setLoading(false);
        if (setWarningEditModel && error.response.status === 409) {
          closeModal();
          setWarningEditModel(true);
          navigate(`/home`);
        } else if (error.response.status === 422) {
          handleValidationError(error.response.data);
        } else {
          enqueueToast({
            variant: "error",
            message: t("orders:editAddressModal.update.failure.toast"),
            isCloseable: true,
          });
          closeModal();
        }
      });
  };

  function hasValidationError() {
    return (
      isCityInvalid ||
      isCountryInvalid ||
      (isStateFieldEnabled() ? isStateInvalid : false) ||
      isZipCodeInvalid ||
      isJurisdictionInvalid ||
      isAddressLineOneInvalid
    );
  }

  const resetModelAddress = () => {
    const resetAddress = (existingAddress: Address) => {
      setName(getValue(existingAddress.name));
      setAddressLineOne(getValue(existingAddress.addressLine1));
      setAddressLineTwo(getValue(existingAddress.addressLine2));
      setCityName(getValue(existingAddress.city));
      setZipCode(getValue(existingAddress.postalCode));
      setPhoneNumber(getValue(existingAddress.phoneNumber));
      setSelectedCountry(getValue(existingAddress.country).toUpperCase());
      setSelectedRegion(getValue(existingAddress.state));
    };

    const resetFlags = () => {
      setCountryInvalid(false);
      setStateInvalid(false);
      setCityInvalid(false);
      setZipCodeInvalid(false);
      setZipCodeValidationDescription("");
      setJurisdictionInvalid(false);
      setAddressLineOneValidation("");
      setIsFormModified(false);
      setConfirmAddress(false);
      setAddressError("");
      setSuggestedList([]);
      setSelected("");
      setHasProcessedInitialValidation(false);
    };

    resetAddress(address);
    resetFlags();
  };

  const getHelperTextForAddressLineOne = (validation: string) =>
    isAddressLineOneInvalid && !isV2AddressValidationEnabled
      ? t(addressLineOneHelperTextMap.get(validation) ?? "")
      : "";

  const getHelperTextForCity = () =>
    isCityInvalid && !isV2AddressValidationEnabled
      ? t("orders:editAddressModal.city.invalid.message")
      : "";

  const getPhoneNumberField = () => (
    <TextField
      label={t("orders:editAddressModal.phone.label")}
      placeholder={t("orders:input.placeholder") ?? "Enter input here..."}
      value={phoneNumber}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(event.target.value.replace(/[^+\- \d()]/gi, ""));
        setIsFormModified(true);
        setIsSelectedFromSuggestion(false);
      }}
      sx={{
        width: "50%",
        paddingRight: theme.tokens.FuelSpacing2,
        "& .MuiInputBase-root": {
          backgroundColor: "white",
          color: "grey",
        },
        "& .MuiInputBase-input": {
          backgroundColor: "white",
          color: "grey",
        },
        "& .MuiInputLabel-root": isReadOnly
          ? {}
          : {
              color: "black",
              opacity: "unset",
            },
      }}
      disabled
      data-testid="edit-address-modal-phoneNumber"
    />
  );

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      fullWidth
      maxWidth="sm"
      sx={
        suggestedList.length > 0
          ? {
              "& .MuiPaper-root": {
                maxHeight: "80vh",
                minHeight: "600px",
              },
            }
          : { style }
      }
      data-testid="edit-address-modal"
      TransitionProps={{
        onEntered: () => {
          if (suggestedList.length > 0 && firstSuggestedRef.current) {
            requestAnimationFrame(() => {
              firstSuggestedRef.current?.focus();
            });
          }
        },
      }}
    >
      <DialogTitle>{t("orders:editAddressModal.title")}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          if (!isCleanserAddressValidationEnabled) resetModelAddress();
          closeModal();
        }}
        size="large"
        sx={{
          position: "absolute",
          right: 15,
          top: 15,
        }}
      >
        <CloseIcon />
      </IconButton>
      {loading && <LoadingIndicator overlayLoader />}
      <DialogContent dividers sx={{ borderBottom: "none" }}>
        <Box
          sx={{
            gap: theme.tokens.FuelSpacing4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isV2AddressValidationEnabled &&
            addressError &&
            (hasProcessedInitialValidation || isFormModified) && (
              <Alert
                variant="error"
                data-testid="address-error"
                title={t(
                  "orders:editAddressModal.address.error.title"
                ).toString()}
              >
                <Typography>
                  {t("orders:editAddressModal.address.error.message.line1")}
                </Typography>
                <Typography>
                  {t("orders:editAddressModal.address.error.message.line2")}
                </Typography>
              </Alert>
            )}
          {isJurisdictionInvalid &&
            (hasProcessedInitialValidation || isFormModified) && (
              <Alert variant="error" data-testid="jurisdiction-error">
                {t("orders:editAddressModal.jurisdiction.invalid.message")}
              </Alert>
            )}
          <TextField
            label={t("orders:editAddressModal.name.label")}
            placeholder={t("orders:input.placeholder") ?? "Enter input here..."}
            value={name}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
              setIsFormModified(true);
              setIsSelectedFromSuggestion(false);
              setConfirmAddress(false);
            }}
            data-testid="edit-address-modal-name"
            disabled={isReadOnly}
          />
          <TextField
            label={t("orders:editAddressModal.address.label")}
            placeholder={t("orders:input.placeholder") ?? "Enter input here..."}
            value={addressLineOne}
            onChange={onAddressLineOneChange}
            error={isAddressLineOneInvalid && !isV2AddressValidationEnabled}
            helperText={getHelperTextForAddressLineOne(
              addressLineOneValidation
            )}
            data-testid="edit-address-modal-addressLine1"
            disabled={isReadOnly}
            sx={{
              ".MuiOutlinedInput-root.Mui-error": {
                border: `solid ${theme.tokens.FuelColorAlertErrorIcon} 1px !important`,
                outline: "none !important",
              },
            }}
          />
          <TextField
            placeholder={t("orders:input.placeholder") ?? "Enter input here..."}
            value={addressLineTwo}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setAddressLineTwo(event.target.value);
              setIsFormModified(true);
              setIsSelectedFromSuggestion(false);
              setConfirmAddress(false);
            }}
            data-testid="edit-address-modal-addressLine2"
            disabled={isReadOnly}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: theme.tokens.FuelSpacing4,
            }}
          >
            <FormControl
              fullWidth
              error={isCountryInvalid}
              disabled={isReadOnly}
            >
              <InputLabel>
                {t("orders:editAddressModal.country.label")}
              </InputLabel>
              <Select
                placeholder={
                  t("orders:editAddressModal.country.placeholder") ??
                  "Select Country"
                }
                value={selectedCountry}
                onChange={onCountryChange}
                data-testid="edit-address-modal-country"
                disabled
              >
                {getCountryOptions()}
              </Select>
              {isCountryInvalid && (
                <FormHelperText id="Country-helperText">
                  {t("orders:editAddressModal.country.invalid.message")}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              label={t("orders:editAddressModal.zipCode.label")}
              placeholder={
                t("orders:input.placeholder") ?? "Enter input here..."
              }
              sx={{
                ".MuiOutlinedInput-root.Mui-error": {
                  border: `solid ${theme.tokens.FuelColorAlertErrorIcon} 1px !important`,
                  outline: "none !important",
                },
              }}
              variant="outlined"
              value={zipCode}
              onChange={onZipCodeChange}
              error={isZipCodeInvalid}
              helperText={isZipCodeInvalid ? getZipCodeValidationMessage() : ""}
              data-testid="edit-address-modal-postalCode"
              disabled={isReadOnly}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: theme.tokens.FuelSpacing4,
            }}
          >
            <TextField
              label={t("orders:editAddressModal.city.label")}
              placeholder={
                t("orders:input.placeholder") ?? "Enter input here..."
              }
              sx={{
                ".MuiOutlinedInput-root.Mui-error": {
                  border: `solid ${theme.tokens.FuelColorAlertErrorIcon} 1px !important`,
                  outline: "none !important",
                },
              }}
              value={cityName}
              onChange={onCityNameChange}
              error={isCityInvalid && !isV2AddressValidationEnabled}
              helperText={getHelperTextForCity()}
              data-testid="edit-address-modal-city"
              disabled={isReadOnly}
            />
            {isStateFieldEnabled() && (
              <FormControl
                fullWidth
                error={isStateInvalid}
                disabled={isReadOnly}
              >
                <InputLabel>
                  {t("orders:editAddressModal.state.label")}
                </InputLabel>
                <Select
                  placeholder={
                    t("orders:editAddressModal.state.placeholder") ??
                    "Select a State or Province"
                  }
                  value={selectedRegion}
                  onChange={onRegionChange}
                  data-testid="edit-address-modal-state"
                  disabled={isReadOnly}
                >
                  {getRegionOptions()}
                </Select>
                {isStateInvalid && (
                  <FormHelperText id="State-helperText">
                    {t("orders:editAddressModal.state.invalid.message")}
                  </FormHelperText>
                )}
              </FormControl>
            )}
            {!isStateFieldEnabled() && getPhoneNumberField()}
          </Box>
          {isStateFieldEnabled() && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: theme.tokens.FuelSpacing4,
                flex: 2,
              }}
            >
              {getPhoneNumberField()}
            </Box>
          )}
        </Box>
        {suggestedList.length > 0 && (
          <Box
            data-testid="suggested-address-list"
            sx={{
              borderRadius: theme.tokens.FuelBorderRadiusMd,
              gap: "16px",
              opacity: 1,
              padding: "16px",
              backgroundColor: "#F7F7F7",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              mt: 2,
            }}
          >
            <Typography fontWeight={600} mb={1}>
              {t("orders:editAddressModal.potential.address.title")}
            </Typography>
            {suggestedList.length > 0 && (
              <RadioGroup
                value={selected}
                onChange={(_, value) => {
                  setSelected(value);
                  const chosen = suggestedList.find(
                    (item) => item.id === value
                  );
                  if (!chosen) return;

                  setName(chosen.name);
                  setAddressLineOne(chosen.addressLineOne);
                  setAddressLineTwo(chosen.addressLineTwo);
                  setCityName(chosen.cityName);
                  setSelectedRegion(chosen.stateOrProvinceName);
                  setZipCode(chosen.zipCode);
                  setPhoneNumber(chosen.phoneNumber);
                  setSelectedCountry(chosen.countryCode);
                  setIsFormModified(true);
                  setIsSelectedFromSuggestion(true);
                  setConfirmAddress(false);
                }}
              >
                {suggestedList.map((item, idx) => (
                  <FormControlLabel
                    sx={{ alignItems: "flex-start" }}
                    key={item.id}
                    value={item.id}
                    control={
                      <Radio
                        inputRef={idx === 0 ? firstSuggestedRef : undefined}
                      />
                    }
                    label={
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {[
                          item.name,
                          item.addressLineOne,
                          item.addressLineTwo,
                          item.cityName,
                          item.stateOrProvinceName,
                          item.zipCode,
                          item.countryCode,
                          item.phoneNumber,
                        ]
                          .filter(Boolean)
                          .join(",\n")}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "stretch",
          gap: 2,
          padding: "16px 24px",
        }}
      >
        {ENABLE_ADDRESS_CONFIRMATION_CHECKBOX &&
          isV2AddressValidationEnabled &&
          hasProcessedInitialValidation &&
          addressError &&
          suggestedList.length === 0 && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmAddress}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setConfirmAddress(event.target.checked);
                      setIsFormModified(true);
                    }}
                    data-testid="confirm-address-checkbox"
                  />
                }
                label={
                  <Typography fontWeight={theme.tokens.FuelFontWeightSemibold}>
                    {t("orders:editAddressModal.confirmAddress.label")}
                  </Typography>
                }
                sx={{ marginLeft: 0 }}
              />
            </>
          )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => {
              setSuggestedList([]);
              if (!isCleanserAddressValidationEnabled) resetModelAddress();
              closeModal();
            }}
            variant="secondary"
            data-testid="modal-close-button"
          >
            {t("common:modal.cancelBtn")}
          </Button>
          <Button
            disabled={
              !confirmAddress &&
              (hasValidationError() ||
                !isFormModified ||
                (suggestedList.length > 0 && !selected) ||
                (hasProcessedInitialValidation && !!addressError))
            }
            onClick={() => {
              if (isCleanserAddressValidationEnabled) {
                handleSubmitWithCleanserValidation();
              } else if (isV2AddressValidationEnabled) {
                handleSubmitV2AddressValidation();
              } else {
                handleSubmit();
              }
            }}
            variant="primary"
            data-testid="modal-save-button"
          >
            {t("common:modal.saveBtn")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditAddressModal;
